import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import { getApiInstance } from "../../../utils/api";
import { nombreFicheroDescarga } from "../../../utils/descargas";
import "./CantidadesGeneradas.scss";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import {
    createFilterState,
    applyFilterState,
} from "../../../lib/reportNew/filter-stateNew";
import { inferObjectFields } from "../../../lib/reportNew/fieldsNew";
import PieChartGraph from "../../../components/common/Charts/PieChartGraph";
import Table from "../../../components/common/Table/Table";
import ScrollableBarChartGraph from "../../../components/common/Charts/ScrollableBarChartGraph";
import Modal from "../../../components/common/Modal/Modal";
import { useZone } from "../../../context/ZoneContext";
import { ZONE_LABEL_MAP } from "../../../constants/zones";
import { MdOutlineFileDownload } from "react-icons/md";
import InputSwitch from "../../../components/common/Inputs/InputSwitch/InputSwitch";
import { Field } from "../../../components/ui-kitNew/controlsNew";
import { formatPeso, formatPesoPorTipo } from "../../../utils/format";
import { codsFilaDe, zonaExportFromFilterState } from "../useExcelExport";

const columnasResiduos = [
    {
        key: "residuo",
        label: "Residuo",
        visible: true,
        sortable: true,
        render: (row) =>
            `${row.codResiduo ?? ""}${row.codResiduo ? " - " : ""}${row.residuo}`,
    },
    {
        key: "pesoTotal",
        label: "Peso Total (kg)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) => new Intl.NumberFormat("es-ES").format(row.pesoTotal),
    },
    {
        key: "volumenTotal",
        label: "Volumen (m³)",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            `${new Intl.NumberFormat("es-ES", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(row.volumenTotal)} m³`,
    },
];

const PROYECTOS_EXCLUIDOS = [3, 5];

export default function CantidadesGeneradas() {
    const [activeTab, setActiveTab] = useState("grafica");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalExcelOpen, setIsModalExcelOpen] = useState(false);
    const [exportando, setExportando] = useState(null);
    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(false);
    const { selectedZone } = useZone();

    const [informeCantidadesGeneradas, setInformeCantidadesGeneradas] =
        useState([]);

    const filterFields = [
        { key: "pais", label: "País", type: "enum" },
        { key: "provincia", label: "Provincia", type: "enum" },
        { key: "nombreObra", label: "Centro de producción", type: "enum" },
        {
            key: "codUbicacion",
            label: "Ubicación",
            type: "enum",
            labelMap: ["", "Calle", "Centro comercial"],
        },
        { key: "proyecto", label: "Proyecto", type: "enum" },
        { key: "anioResiduo", label: "Año", type: "enum" },
        { key: "codObra", label: "Código de obra", type: "enum" },
        { key: "intermediario", label: "Intermediario", type: "enum" },
        { key: "tipoObra", label: "Tipo de obra", type: "enum" },
        {
            key: "rangoFechas",
            label: "Fecha fin",
            type: "dateRange",
            rangeEndField: "fechaFin",
        },
        {
            key: "codZona",
            label: "Zona",
            type: "enum",
            labelMap: ZONE_LABEL_MAP,
        },
    ];

    const defaultQuickFilters = [
        "pais",
        "provincia",
        "codUbicacion",
        "proyecto",
        "intermediario",
        "tipoObra",
        "rangoFechas",
        "codZona",
    ];

    const [filterState, setFilterState] = useState(() =>
        createFilterState(defaultQuickFilters)
    );

    const getYearFromFechaResiduo = (item) => {
        const fecha = item.fechaResiduo;

        if (!fecha) return null;

        const year = new Date(fecha).getFullYear();

        return year > 1950 && year <= new Date().getFullYear()
            ? year
            : null;
    };

    useEffect(() => {
        const fetchInformeCantidadesGeneradas = async () => {
            setLoading(true);
            setError(null);
            try {
                const api = getApiInstance();
                const response = await api.get(
                    `/api/Informe/GetCantidadesGeneradas`
                );
                setInformeCantidadesGeneradas(response.data);
            } catch (err) {
                setError(
                    "No se ha podido cargar el informe. Vuelve a intentarlo más tarde."
                );
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInformeCantidadesGeneradas();
    }, [selectedZone?.value]);

    const datosNormalizados = useMemo(() => {
        return informeCantidadesGeneradas.map((item) => {
            const anioResiduo = getYearFromFechaResiduo(item);

            return {
                ...item,
                proyectoFinal: item.proyecto || item.nombreObra,
                anioResiduo,
            };
        });
    }, [informeCantidadesGeneradas]);

    const allFilterFields = useMemo(() => {
        const seen = new Set(filterFields.map((f) => f.key))
        return [...filterFields, ...inferObjectFields(datosNormalizados).filter((f) => !seen.has(f.key))]
    }, [filterFields, datosNormalizados])

    /* El quick filter de proyecto guarda el nombre; el backend crea el Excel con
       int CodProyecto, así que hace falta traducir el nombre a su código. */
    const proyectoACodigo = useMemo(() => {
        const mapa = {};
        datosNormalizados.forEach((item) => {
            if (
                item.proyecto != null &&
                item.codProyecto != null &&
                mapa[item.proyecto] == null
            ) {
                mapa[item.proyecto] = item.codProyecto;
            }
        });
        return mapa;
    }, [datosNormalizados]);

    const datosFiltrados = useMemo(() => {
        return applyFilterState(datosNormalizados, filterState, allFilterFields)
            .filter((item) => {
                if (!excluirRaeeZeroWaste) return true;
                const codProyecto = Number(item.codProyecto);
                return !PROYECTOS_EXCLUIDOS.includes(codProyecto);
            });
    }, [datosNormalizados, filterState, allFilterFields, excluirRaeeZeroWaste]);

    const {
        datosTabla,
        datosBarChart,
        datosPieChart,
        totalCantidadGenerada,
        totalValorizacion,
        totalEliminacion,
        porcentajeValorizacion,
        porcentajeEliminacion,
    } = useMemo(() => {
        const datosAgrupadosTabla = Object.values(
            datosFiltrados.reduce((acc, item) => {
                if (!acc[item.residuo]) {
                    acc[item.residuo] = {
                        codResiduo: item.codResiduo,
                        residuo: item.residuo,
                        pesoTotal: 0,
                        cantidadValorizada: 0,
                        cantidadEliminada: 0,
                        volumenTotal: 0,
                    };
                }
                acc[item.residuo].pesoTotal += item.pesoTotal;
                acc[item.residuo].cantidadValorizada += item.cantidadValorizada;
                acc[item.residuo].cantidadEliminada += item.cantidadEliminada;
                acc[item.residuo].volumenTotal += item.volumenTotal;
                return acc;
            }, {})
        );

        const agregadosBar = datosFiltrados.reduce((acc, item) => {
            if (!acc[item.residuo]) {
                acc[item.residuo] = {
                    pesoTotal: 0,
                    cantidadValorizada: 0,
                    peligroso: item.esPeligroso,
                };
            }
            acc[item.residuo].peligroso ??= item.esPeligroso;
            acc[item.residuo].pesoTotal += item.pesoTotal;
            acc[item.residuo].cantidadValorizada += item.cantidadValorizada;
            return acc;
        }, {});

        const datosProcesadosBar = Object.keys(agregadosBar)
            .map((residuo) => {
                const pesoTotal = agregadosBar[residuo].pesoTotal;
                const cantidadValorizada =
                    agregadosBar[residuo].cantidadValorizada;
                const pctValorizacion =
                    pesoTotal > 0
                        ? (cantidadValorizada / pesoTotal) * 100
                        : 0;

                return {
                    name: residuo,
                    "Cantidad generada": pesoTotal,
                    Valorización: cantidadValorizada,
                    pctValorizacion,
                    peligroso: agregadosBar[residuo]?.peligroso,
                };
            })
            .sort(
                (a, b) =>
                    b["Cantidad generada"] +
                    b.Valorización -
                    (a["Cantidad generada"] + a.Valorización)
            );

        const totalVal = datosFiltrados.reduce(
            (sum, item) => sum + item.cantidadValorizada,
            0
        );
        const totalElim = datosFiltrados.reduce(
            (sum, item) => sum + item.cantidadEliminada,
            0
        );
        const granTotal = totalVal + totalElim;
        const porcVal = granTotal > 0 ? (totalVal / granTotal) * 100 : 0;
        const porcElim = granTotal > 0 ? (totalElim / granTotal) * 100 : 0;
        const datosProcesadosPie = [
            { name: "Valorización", value: parseFloat(porcVal.toFixed(2)) },
            { name: "Eliminación", value: parseFloat(porcElim.toFixed(2)) },
        ];

        const totalGenerado = datosFiltrados.reduce(
            (sum, item) => sum + item.pesoTotal,
            0
        );

        return {
            datosTabla: datosAgrupadosTabla,
            datosBarChart: datosProcesadosBar,
            datosPieChart: datosProcesadosPie,
            totalCantidadGenerada: totalGenerado,
            totalValorizacion: totalVal,
            totalEliminacion: totalElim,
            porcentajeValorizacion: porcVal.toFixed(1),
            porcentajeEliminacion: porcElim.toFixed(1),
        };
    }, [datosFiltrados]);

    const formatKg = (value) => new Intl.NumberFormat("es-ES").format(value);

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "listado", label: "Listado" },
    ];

    function pad2(n) {
        return String(n).padStart(2, "0");
    }

    function timestampUTCYYYYMMDDHHmmss(date = new Date()) {
        const Y = date.getUTCFullYear();
        const M = pad2(date.getUTCMonth() + 1);
        const D = pad2(date.getUTCDate());
        const h = pad2(date.getUTCHours());
        const m = pad2(date.getUTCMinutes());
        const s = pad2(date.getUTCSeconds());
        return `${Y}${M}${D}${h}${m}${s}`;
    }

    const handleExportToExcel = async (tipo) => {
        if (exportando) return;
        setExportando(tipo);
        const api = getApiInstance();
        const codZonaLegalMap = { 1: 1, 2: 2, 3: 2 };
        const codZona = selectedZone.value;
        const codZonaLegal =
            codZonaLegalMap[Number(selectedZone?.value)] ?? 1;
        const zonaExport = zonaExportFromFilterState(filterState);

        try {
            var apiUrl = "api/Informe/CreateReportExcelCompleto";
            if (tipo === "resumen") {
                apiUrl = "api/Informe/CreateReportExcelCompletoAgrupado";
            }

            const codObra =
                filterState.quick.codObra?.[0] != null
                    ? filterState.quick.codObra[0]
                    : "0";
            const proyectoVal = (() => {
                const nombre =
                    filterState.quick.proyecto?.[0] != null
                        ? filterState.quick.proyecto[0]
                        : null;
                if (nombre == null) return "0";
                if (/^\d+$/.test(String(nombre)))
                    return String(nombre);
                return proyectoACodigo[nombre] != null
                    ? String(proyectoACodigo[nombre])
                    : "0";
            })();
            const fechaVal =
                filterState.quick.anioResiduo?.[0] != null
                    ? filterState.quick.anioResiduo[0]
                    : "0";

            const query = new URLSearchParams({
                CodObra: codObra,
                CodProyecto: proyectoVal,
                codFecha: fechaVal,
                CodTipoObra: 0,
                codZona: zonaExport.codZona ?? codZona,
                codZonaLegal: zonaExport.codZonaLegal ?? codZonaLegal,
            });

            const finalUrl = `${apiUrl}?${query.toString()}`;

            /* Los identificadores de las filas que se están viendo, ya filtradas. El
               backend monta el Excel solo con ellas, así que pantalla y fichero
               coinciden. Se mandan con los dos exports: el Completo también filtra
               por el cuerpo, como hace Costes con su resumen. */
            const codsFila = codsFilaDe(datosFiltrados);

            const response = await api.post(finalUrl, codsFila, {
                responseType: "blob",
            });

            const fileName = nombreFicheroDescarga(
                response.headers["content-disposition"],
                `Informe_Cantidades_Obra_${codObra || "0"}__${timestampUTCYYYYMMDDHHmmss()}.xlsx`
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(
                "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setExportando(null);
        }
    };

    return (
        <PageLayout
            title="Cantidades generadas por centro de producción"
            subtitle="Análisis detallado de los residuos generados y su tratamiento"
            actions={[
                <Button
                    key="export"
                    componentType="button"
                    variant="filled"
                    startIcon={<FiDownload />}
                    onClick={() => setIsModalExcelOpen(true)}
                >
                    Exportar a excel
                </Button>,
            ]}
        >
            {error ? (
                <div
                    className="error-message"
                    style={{
                        padding: "40px",
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#d32f2f",
                    }}
                >
                    {error}
                </div>
            ) : (
                <>
                    <TabsNavigation
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />

                    {loading ? (
                        activeTab === "listado" ? (
                            <div
                                className="informe-table-skeleton"
                                aria-hidden="true"
                            >
                                <div className="informe-table-skeleton__row" />
                                <div className="informe-table-skeleton__row" />
                                <div className="informe-table-skeleton__row" />
                                <div className="informe-table-skeleton__row" />
                            </div>
                        ) : (
                            <div
                                className="informe-chart-skeleton informe-chart-skeleton--bar"
                                aria-hidden="true"
                            >
                                <div className="informe-chart-skeleton__bars">
                                    {[80, 55, 70, 40, 62, 30, 48].map(
                                        (h, i) => (
                                            <div
                                                key={i}
                                                className="informe-chart-skeleton__bar"
                                                style={{ height: `${h}%` }}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        )
                    ) : activeTab === "grafica" ? (
                        <div className="cantidades-generadas">
                            <FilterBar
                                rows={datosNormalizados}
                                state={filterState}
                                onChange={setFilterState}
                                resultCount={datosFiltrados.length}
                                totalCount={datosNormalizados.length}
                                fields={filterFields}
                                excludeFields={["fechaFin", "codTipoObra", "codProyecto", "codTransportista", "codGestorFinal", "codPais", "codIntermediario", "ubicacion", "codProvincia", "proyectoFinal", "codFila"]}
                                headerExtra={
                                    <Field label="Excluir RAAES / Zero Waste">
                                        <div className="abono-toggle">
                                            <InputSwitch
                                                checked={excluirRaeeZeroWaste}
                                                onChange={(isOn) =>
                                                    setExcluirRaeeZeroWaste(isOn)
                                                }
                                            />
                                            <span
                                                className={`abono-toggle__label ${
                                                    excluirRaeeZeroWaste
                                                        ? "abono-toggle__label--active"
                                                        : ""
                                                }`}
                                            >
                                                Excluír RAAES / Zero Waste
                                            </span>
                                        </div>
                                    </Field>
                                }
                            />

                            <div className="charts-grid">
                                <div className="chart-card">
                                    <div className="chart-card__header">
                                        <div>
                                            <h3 className="chart-card__title">
                                                Cantidad generada por Residuo
                                            </h3>
                                            <p className="chart-card__subtitle">
                                                Distribución de residuos por
                                                tipo y tratamiento
                                            </p>
                                        </div>
                                        <div className="chart-card__total">
                                            <span className="chart-card__total-value">
                                                {formatPeso(
                                                    totalCantidadGenerada
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="chart-card__body">
                                        <ScrollableBarChartGraph
                                            data={datosBarChart}
                                            passItemToFormatter
                                            valueFormatter={(value, item) =>
                                                formatPesoPorTipo(value, Boolean(item?.peligroso))
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="chart-card chart-card--compact">
                                    <div className="chart-card__header">
                                        <h3 className="chart-card__title">
                                            Distribución Global
                                        </h3>
                                    </div>

                                    <div className="chart-card__body">
                                        <PieChartGraph
                                            data={{ all: datosPieChart }}
                                            activeTab="all"
                                            tooltipValueFormatter={formatPeso}
                                        />
                                    </div>

                                    <div className="chart-card__legend">
                                        <div className="legend-item legend-item--success">
                                            <div className="legend-item__dot"></div>
                                            <div className="legend-item__content">
                                                <span className="legend-item__label">
                                                    Valorización
                                                </span>
                                                <span className="legend-item__value">
                                                    {formatPeso(
                                                        totalValorizacion
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="legend-item legend-item--danger">
                                            <div className="legend-item__dot"></div>
                                            <div className="legend-item__content">
                                                <span className="legend-item__label">
                                                    Eliminación
                                                </span>
                                                <span className="legend-item__value">
                                                    {formatPeso(totalEliminacion)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="cantidades-generadas">
                            <FilterBar
                                rows={datosNormalizados}
                                state={filterState}
                                onChange={setFilterState}
                                resultCount={datosFiltrados.length}
                                totalCount={datosNormalizados.length}
                                fields={filterFields}
                                excludeFields={["fechaFin", "codTipoObra", "codProyecto", "codTransportista", "codGestorFinal", "codPais", "codIntermediario", "codUbicacion", "codProvincia"]}
                                headerExtra={
                                    <Field label="Excluir RAAES / Zero Waste">
                                        <div className="abono-toggle">
                                            <InputSwitch
                                                checked={excluirRaeeZeroWaste}
                                                onChange={(isOn) =>
                                                    setExcluirRaeeZeroWaste(isOn)
                                                }
                                            />
                                            <span
                                                className={`abono-toggle__label ${
                                                    excluirRaeeZeroWaste
                                                        ? "abono-toggle__label--active"
                                                        : ""
                                                }`}
                                            >
                                                Excluír RAAES / Zero Waste
                                            </span>
                                        </div>
                                    </Field>
                                }
                            />

                            <div className="table-wrapper">
                                <Table
                                    columns={columnasResiduos}
                                    dataTable={datosTabla}
                                    keyProp="residuo"
                                    sortProp="residuo"
                                    hideFilterBar
                                    searchTerms={[
                                        "residuo",
                                        "pesoTotal",
                                        "volumenTotal",
                                    ]}
                                    showCheckboxes={false}
                                    noResultsMessage="No se han encontrado residuos con los filtros aplicados."
                                    elementName="tipo de residuo"
                                    loading={loading}
                                    rowsPerPage={10}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
            <Modal
                isOpen={isModalExcelOpen}
                onClose={() => setIsModalExcelOpen(false)}
                title="Exportar informe a Excel"
                actions={[
                    <Button
                        key="export-resumen"
                        className="export-excel__btn export-excel__btn--primary"
                        onClick={() => handleExportToExcel("resumen")}
                        startIcon={<MdOutlineFileDownload />}
                        disabled={exportando}
                    >
                        {exportando === "resumen"
                            ? "Generando..."
                            : "Informe Cantidades Generadas"}
                    </Button>,
                    <Button
                        key="export-completo"
                        className="export-excel__btn export-excel__btn--secondary"
                        onClick={() => handleExportToExcel("completo")}
                        startIcon={<MdOutlineFileDownload />}
                        disabled={exportando}
                    >
                        {exportando === "completo"
                            ? "Generando..."
                            : "Informe Completo"}
                    </Button>,
                ]}
            >
                <div className="export-excel__actions"></div>
            </Modal>
        </PageLayout>
    );
}
